import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  SkipNext as SkipIcon,
  Warning as DeviationIcon,
  Timer as TimerIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { statusColors } from '../App';
import { apiService } from '../services/apiService';

interface BatchDetails {
  id: string;
  batch_number: string;
  status: string;
  product_name: string;
  mbr_number: string;
  line_name: string;
  batch_size_value: number;
  batch_size_unit: string;
  actual_start: string | null;
  actual_end: string | null;
  notes: string;
}

interface BatchStepParameter {
  id: string;
  parameter_name: string;
  parameter_type: string;
  unit: string | null;
  target_value: string | null;
  min_value: number | null;
  max_value: number | null;
  recorded_value: string | null;
  recorded_value_numeric: number | null;
  is_in_spec: boolean | null;
  is_critical: boolean;
}

interface BatchStep {
  id: string;
  step_number: number;
  step_name: string;
  step_instructions: string;
  expected_duration_minutes: number;
  is_critical: boolean;
  requires_signature: boolean;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  performed_by: string | null;
  verified_by: string | null;
  notes: string;
  parameters: BatchStepParameter[];
}

interface Deviation {
  id: string;
  deviation_number: string;
  severity: string;
  description: string;
  status: string;
  reported_at: string;
  reported_by: string;
}

const BatchExecution: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const [batch, setBatch] = useState<BatchDetails | null>(null);
  const [batchSteps, setBatchSteps] = useState<BatchStep[]>([]);
  const [deviations, setDeviations] = useState<Deviation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState<BatchStep | null>(null);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [completingStep, setCompletingStep] = useState<string | null>(null);
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopReason, setStopReason] = useState('');

  useEffect(() => {
    if (batchId) {
      fetchBatchDetails();
    }
  }, [batchId]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const data = await apiService.getBatchExecutionDetails(batchId!);
      setBatch(data.batch);
      setBatchSteps(data.batch_steps);
      setDeviations(data.deviations);
    } catch (error) {
      console.error('Error fetching batch details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStep = async (stepId: string) => {
    try {
      setCompletingStep(stepId);
      await apiService.completeBatchStep(stepId);
      await fetchBatchDetails(); // Refresh data
    } catch (error) {
      console.error('Error completing step:', error);
    } finally {
      setCompletingStep(null);
    }
  };

  const handleSkipStep = async () => {
    if (!selectedStep) return;
    
    try {
      await apiService.skipBatchStep(selectedStep.id, skipReason);
      setSkipDialogOpen(false);
      setSkipReason('');
      setSelectedStep(null);
      await fetchBatchDetails(); // Refresh data
    } catch (error) {
      console.error('Error skipping step:', error);
    }
  };

  // Batch-level controls
  const handleBatchAction = async (action: 'start' | 'pause' | 'resume' | 'stop') => {
    if (!batch) return;
    setBatchActionLoading(true);
    try {
      const statusMap: Record<string, string> = {
        start: 'in_progress',
        pause: 'paused',
        resume: 'in_progress',
        stop: 'completed',
      };
      await fetch(`/api/batches/${batchId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: statusMap[action],
          ...(action === 'stop' ? { actual_end: new Date().toISOString(), notes: stopReason || undefined } : {}),
          ...(action === 'start' || action === 'resume' ? { actual_start: batch.actual_start || new Date().toISOString() } : {}),
        }),
      });
      setStopDialogOpen(false);
      setStopReason('');
      await fetchBatchDetails();
    } catch (e) {
      console.error('Batch action failed:', e);
    }
    setBatchActionLoading(false);
  };

  const openSkipDialog = (step: BatchStep) => {
    setSelectedStep(step);
    setSkipDialogOpen(true);
  };

  const closeSkipDialog = () => {
    setSkipDialogOpen(false);
    setSkipReason('');
    setSelectedStep(null);
  };

  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || '#6B7280';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not started';
    return new Date(dateString).toLocaleString();
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompleteIcon sx={{ color: statusColors.completed }} />;
      case 'in_progress':
        return <TimerIcon sx={{ color: statusColors.in_progress }} />;
      case 'skipped':
        return <SkipIcon sx={{ color: statusColors.pending }} />;
      case 'failed':
        return <DeviationIcon sx={{ color: statusColors.rejected }} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!batch) {
    return (
      <Box>
        <Alert severity="error">
          Batch not found or failed to load
        </Alert>
      </Box>
    );
  }

  const activeStepIndex = batchSteps.findIndex(step => step.status === 'pending' || step.status === 'in_progress');
  const completedSteps = batchSteps.filter(step => step.status === 'completed').length;
  const totalSteps = batchSteps.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h3" component="h1">
          Batch Execution
        </Typography>

        {/* Play / Pause / Stop Controls */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {(batch.status === 'pending') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayIcon />}
              onClick={() => handleBatchAction('start')}
              disabled={batchActionLoading}
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
            >
              Start Batch
            </Button>
          )}
          {(batch.status === 'paused') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayIcon />}
              onClick={() => handleBatchAction('resume')}
              disabled={batchActionLoading}
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
            >
              Resume
            </Button>
          )}
          {(batch.status === 'in_progress') && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={() => handleBatchAction('pause')}
              disabled={batchActionLoading}
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
            >
              Pause
            </Button>
          )}
          {(batch.status === 'in_progress' || batch.status === 'paused') && (
            <Button
              variant="contained"
              color="error"
              startIcon={<StopIcon />}
              onClick={() => setStopDialogOpen(true)}
              disabled={batchActionLoading}
              sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
            >
              Stop
            </Button>
          )}
          {(batch.status === 'completed' || batch.status === 'approved') && (
            <Chip
              label={batch.status === 'approved' ? '✓ Approved' : '✓ Completed'}
              sx={{ backgroundColor: '#10B98130', color: '#10B981', fontWeight: 'bold', fontSize: '0.9rem', height: 36 }}
            />
          )}
        </Box>
      </Box>

      {/* Batch Header Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                {batch.batch_number}
              </Typography>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                {batch.product_name}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip
                  label={batch.status.replace('_', ' ')}
                  sx={{
                    backgroundColor: getStatusColor(batch.status),
                    color: 'white',
                    textTransform: 'capitalize',
                    fontWeight: 'medium',
                  }}
                />
                <Typography variant="body2">
                  Progress: {completedSteps}/{totalSteps} steps
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    MBR Number
                  </Typography>
                  <Typography variant="body1">
                    {batch.mbr_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Line
                  </Typography>
                  <Typography variant="body1">
                    {batch.line_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Batch Size
                  </Typography>
                  <Typography variant="body1">
                    {batch.batch_size_value} {batch.batch_size_unit}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Started
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(batch.actual_start)}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Process Steps */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Process Steps
              </Typography>
              
              <Stepper orientation="vertical" activeStep={activeStepIndex}>
                {batchSteps.map((step) => (
                  <Step key={step.id}>
                    <StepLabel
                      icon={getStepStatusIcon(step.status)}
                      StepIconProps={{
                        sx: {
                          color: getStatusColor(step.status),
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6">
                          Step {step.step_number}: {step.step_name}
                        </Typography>
                        {step.is_critical && (
                          <Chip
                            label="Critical"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" paragraph>
                          {step.step_instructions}
                        </Typography>
                        
                        {step.expected_duration_minutes && (
                          <Typography variant="body2" color="textSecondary">
                            Expected duration: {step.expected_duration_minutes} minutes
                          </Typography>
                        )}
                        
                        {step.performed_by && (
                          <Typography variant="body2" color="textSecondary">
                            Performed by: {step.performed_by}
                          </Typography>
                        )}
                        
                        {step.completed_at && (
                          <Typography variant="body2" color="textSecondary">
                            Completed: {formatDate(step.completed_at)}
                          </Typography>
                        )}
                        
                        {step.notes && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            {step.notes}
                          </Alert>
                        )}

                        {step.verified_by && (
                          <Typography variant="body2" color="textSecondary">
                            Verified by: {step.verified_by}
                          </Typography>
                        )}

                        {/* Parameters Table */}
                        {step.parameters && step.parameters.length > 0 && (
                          <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Parameter</TableCell>
                                  <TableCell>Target</TableCell>
                                  <TableCell>Min</TableCell>
                                  <TableCell>Max</TableCell>
                                  <TableCell>Unit</TableCell>
                                  <TableCell>Recorded</TableCell>
                                  <TableCell>In Spec?</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {step.parameters.map((param) => (
                                  <TableRow key={param.id}>
                                    <TableCell>
                                      {param.parameter_name}
                                      {param.is_critical && (
                                        <Chip label="Critical" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
                                      )}
                                    </TableCell>
                                    <TableCell>{param.target_value || '—'}</TableCell>
                                    <TableCell>{param.min_value != null ? param.min_value : '—'}</TableCell>
                                    <TableCell>{param.max_value != null ? param.max_value : '—'}</TableCell>
                                    <TableCell>{param.unit || '—'}</TableCell>
                                    <TableCell>{param.recorded_value || '—'}</TableCell>
                                    <TableCell>
                                      {param.is_in_spec === null ? '—' : param.is_in_spec ? (
                                        <Chip label="Yes" size="small" color="success" />
                                      ) : (
                                        <Chip label="No" size="small" color="error" />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                      
                      {step.status === 'pending' && (
                        <Box display="flex" gap={2}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CompleteIcon />}
                            onClick={() => handleCompleteStep(step.id)}
                            disabled={completingStep === step.id}
                          >
                            {completingStep === step.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              'Complete Step'
                            )}
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<SkipIcon />}
                            onClick={() => openSkipDialog(step)}
                          >
                            Skip Step
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Deviations */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Deviations
              </Typography>
              
              {deviations.length === 0 ? (
                <Typography color="textSecondary">
                  No deviations reported
                </Typography>
              ) : (
                <Box>
                  {deviations.map((deviation) => (
                    <Paper
                      key={deviation.id}
                      elevation={1}
                      sx={{ p: 2, mb: 2, backgroundColor: 'background.paper' }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Typography variant="subtitle2">
                          {deviation.deviation_number}
                        </Typography>
                        <Chip
                          label={deviation.severity}
                          size="small"
                          color={deviation.severity === 'critical' ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" paragraph>
                        {deviation.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Reported by {deviation.reported_by} on {formatDate(deviation.reported_at)}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stop Batch Dialog */}
      <Dialog open={stopDialogOpen} onClose={() => setStopDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#EF4444' }}>
          <StopIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Stop Batch — {batch.batch_number}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will end batch execution. Incomplete steps will remain in their current state.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for stopping (optional)"
            value={stopReason}
            onChange={(e) => setStopReason(e.target.value)}
            margin="normal"
            placeholder="Early completion, equipment issue, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleBatchAction('stop')}
            variant="contained"
            color="error"
            disabled={batchActionLoading}
          >
            Stop Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip Step Dialog */}
      <Dialog open={skipDialogOpen} onClose={closeSkipDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Skip Step</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to skip "{selectedStep?.step_name}"?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for skipping"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSkipDialog}>Cancel</Button>
          <Button onClick={handleSkipStep} variant="contained" color="warning">
            Skip Step
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchExecution;